/**
 * Presents server-paginated ad rows for one publisher ad group while leaving
 * query lifecycle and selected-ad handling with the parent tree row.
 */
import React from 'react';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import DataTable, {
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
} from '@amzn/meridian/data-table';
import Link from '@amzn/meridian/link';
import MeridianLoader from '@amzn/meridian/loader';
import Pagination from '@amzn/meridian/pagination';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { Loader } from 'src/components/common/Loader';
import { ShowingCount } from 'src/components/common/data-table-action-bar/ShowingCount';
import type {
  PaginatedMetaPaginationDocumented,
  PublisherEntityDto,
} from 'src/store/generated/leela-api-generated';
import { narrowAdMetadata } from 'src/utils/publisherEntityMetadata';
import { BADGE_LABELS, derivePublisherEntityStatus } from 'src/utils/publisherEntityStatus';
import { EntityAssetStrip } from './EntityAssetStrip';
import { EntityIcon } from './EntityIcon';
import { EntityMetadataDetails } from './EntityMetadataDetails';
import type { AdsStatusFilter } from './statusFilterParams';
import { EMPTY_VALUE } from './StatusBadge';

const STATUS_DOT_COLORS: Record<string, { bg: string; text: string }> = {
  LIVE: { bg: '#dcfce7', text: '#166534' },
  PENDING: { bg: '#fef9c3', text: '#854d0e' },
  FAILED: { bg: '#fee2e2', text: '#991b1b' },
  SKIPPED: { bg: '#e0e7ff', text: '#3730a3' },
  REMOVED: { bg: '#f3f4f6', text: '#374151' },
};

const CompactStatusDot: React.FC<{ entity: PublisherEntityDto }> = ({ entity }) => {
  const { t } = useTranslation();
  const status = derivePublisherEntityStatus(entity);
  const style = STATUS_DOT_COLORS[status] ?? { bg: '#f3f4f6', text: '#374151' };
  return (
    <span style={{ backgroundColor: style.bg, borderRadius: 12, color: style.text, display: 'inline-block', fontSize: 12, fontWeight: 500, padding: '2px 8px', whiteSpace: 'nowrap' }}>
      {t(BADGE_LABELS[status])}
    </span>
  );
};

interface EntityChildTableProps {
  campaignId: string;
  data: PublisherEntityDto[];
  isError: boolean;
  isFetching: boolean;
  isLoading: boolean;
  page: number;
  pagination: PaginatedMetaPaginationDocumented;
  selectedAdId?: string;
  showDivider?: boolean;
  statusFilter: AdsStatusFilter;
  onPageChange: (page: number) => void;
  onSelectAd: (entity: PublisherEntityDto) => void;
  onStatusFilterChange: (value: AdsStatusFilter) => void;
}

export function formatTextList (values: string[] | undefined): string {
  if (values == null || values.length === 0) return EMPTY_VALUE;
  return values.map(v => typeof v === 'object' ? (v as { text?: string }).text ?? '' : String(v)).filter(Boolean).join(', ') || EMPTY_VALUE;
}

export function filterMetadataKeys (metadata: Record<string, unknown> | undefined, keysToRemove: string[]): Record<string, unknown> | undefined {
  if (metadata == null) return undefined;
  const filtered: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(metadata)) {
    if (!keysToRemove.includes(k) && v !== undefined && v !== null) filtered[k] = v;
  }
  return Object.keys(filtered).length > 0 ? filtered : undefined;
}

const AdDetailContent: React.FC<{ entity: PublisherEntityDto }> = ({ entity }) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = React.useState(false);

  // Keys hidden from display (already shown in the table row)
  const hiddenKeys = ['name', 'gti', 'refTag'];
  const rawMetadata = entity.publisherMetadata as Record<string, unknown> | undefined;

  return (
    <Column data-testid={`ads-manager-ad-detail-${entity._id}`} spacing="300">
      <Link
        data-testid={`ads-manager-ad-show-details-${entity._id}`}
        type="secondary"
        onClick={() => { setShowDetails(prev => !prev); }}
      >
        {t('ads_manager_details')}
      </Link>
      {showDetails && (
        <Column spacing="300">
          {entity.lastError != null && (
            <Row spacing="200" widths={['240px', 'fill']}>
              <Text color="secondary" type="b100">{t('ads_manager_col_last_error')}</Text>
              <Text color="error" type="b200">{entity.lastError}</Text>
            </Row>
          )}
          <EntityMetadataDetails
            layout="row"
            metadata={filterMetadataKeys(rawMetadata, hiddenKeys)}
            testId={`ads-manager-ad-metadata-${entity._id}`}
          />
        </Column>
      )}
    </Column>
  );
};

export const EntityChildTable: React.FC<EntityChildTableProps> = ({
  campaignId,
  data,
  isError,
  isFetching,
  isLoading,
  onPageChange,
  onSelectAd,
  onStatusFilterChange,
  page,
  pagination,
  selectedAdId,
  showDivider = true,
  statusFilter,
}) => {
  const { t } = useTranslation();
  const emptyStateKey = statusFilter !== 'ALL'
    ? 'ads_manager_no_filter_results'
    : 'ads_manager_no_ads_in_ad_group';

  return (
    <Loader isLoading={isLoading}>
      <Column spacing="300">
        {showDivider && (
          <Column spacing="200" spacingInset="400 none none none">
            <Text type="h100">{t('ads_manager_ads_header')}</Text>
            <Divider />
          </Column>
        )}
        <Row alignmentHorizontal="justify" alignmentVertical="center">
          <Row alignmentVertical="center" spacing="300">
            {!showDivider && <Text type="h100">{t('ads_manager_ads_header')}</Text>}
          </Row>
          <Row alignmentVertical="center" spacing="300">
            <ShowingCount
              page={page}
              pageSize={pagination.pageSize}
              total={pagination.total}
            />
            {isFetching && !isLoading && <MeridianLoader size="small" type="linear" />}
          </Row>
        </Row>
        <DataTable
          data-testid="ads-manager-ads-table"
          indentColumn={0}
          showBorder={false}
          showRowDividers={false}
          size="large"
          stickyHeaderRows={true}
          width="100%"
        >
          <DataTableHead backgroundColor="secondary">
            <DataTableRow>
              <DataTableHeader id="name" width={280}>{t('ads_manager_col_ad_name')}</DataTableHeader>
              <DataTableHeader width={180}>{t('ads_manager_col_gti')}</DataTableHeader>
              <DataTableHeader width={200}>
                <Row alignmentVertical="center" spacing="200">
                  <Text type="b300">{t('ads_manager_col_status')}</Text>
                  <Select
                    data-testid="ads-manager-status-filter"
                    size="small"
                    value={statusFilter}
                    width={100}
                    onChange={onStatusFilterChange}
                  >
                    <SelectOption label={t('ads_manager_filter_all')} value="ALL" />
                    <SelectOption label={t('ads_manager_filter_live')} value="LIVE" />
                    <SelectOption label={t('ads_manager_filter_pending')} value="PENDING" />
                    <SelectOption label={t('ads_manager_filter_processing')} value="PROCESSING" />
                    <SelectOption label={t('ads_manager_filter_failed')} value="FAILED" />
                    <SelectOption label={t('ads_manager_filter_removed')} value="REMOVED" />
                  </Select>
                </Row>
              </DataTableHeader>
              <DataTableHeader width={240}>{t('ads_manager_col_entity_id')}</DataTableHeader>
            </DataTableRow>
          </DataTableHead>
          <DataTableBody>
            {!isError && data.length === 0 && (
              <DataTableRow>
                <DataTableCell colSpan={4}>{t(emptyStateKey)}</DataTableCell>
              </DataTableRow>
            )}
            {data.map((entity) => {
              const metadata = narrowAdMetadata(entity.publisherMetadata);
              const adLabel = metadata?.name ?? metadata?.headlines?.[0] ?? metadata?.gti;
              const adGroupSuffix = entity.parentPublisherEntityId
                ? ` (${entity.parentPublisherEntityId.slice(-6)})`
                : '';
              const displayLabel = adLabel ? `${adLabel}${adGroupSuffix}` : EMPTY_VALUE;
              const isSelected = entity._id === selectedAdId;
              const childRowId = `${entity._id}-assets`;

              return (
                <React.Fragment key={entity._id}>
                  <DataTableRow
                    childrenIds={[childRowId]}
                    data-testid={`ads-manager-ad-row-${entity._id}`}
                    highlightOnHover={true}
                    isExpanded={isSelected}
                    rowId={entity._id}
                  >
                    <DataTableCell hasExpander={true} onExpand={() => { onSelectAd(entity); }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <EntityIcon type={entity.publisherEntityType} />
                        <Column spacing="none">
                          <Text>{displayLabel}</Text>
                          {entity.lastError != null && (
                            <Text color="error" type="b100">{entity.lastError}</Text>
                          )}
                        </Column>
                      </div>
                    </DataTableCell>
                    <DataTableCell><div style={{ cursor: 'pointer' }} onClick={() => { onSelectAd(entity); }}>{metadata?.gti ?? EMPTY_VALUE}</div></DataTableCell>
                    <DataTableCell><div style={{ cursor: 'pointer' }} onClick={() => { onSelectAd(entity); }}><CompactStatusDot entity={entity} /></div></DataTableCell>
                    <DataTableCell><div style={{ cursor: 'pointer' }} onClick={() => { onSelectAd(entity); }}>{entity.publisherEntityId ?? EMPTY_VALUE}</div></DataTableCell>
                  </DataTableRow>
                  {isSelected && (
                    <DataTableRow
                      data-testid={`ads-manager-ad-detail-row-${entity._id}`}
                      depth={1}
                      parentId={entity._id}
                      rowId={childRowId}
                    >
                      <DataTableCell colSpan={4}>
                        <Column spacing="400">
                          <AdDetailContent entity={entity} />
                          <EntityAssetStrip
                            adSourceEntityId={entity.sourceEntityId}
                            campaignId={campaignId}
                          />
                        </Column>
                      </DataTableCell>
                    </DataTableRow>
                  )}
                </React.Fragment>
              );
            })}
          </DataTableBody>
        </DataTable>
        {pagination.total > 0 && (
          <Row alignmentHorizontal="end">
            <Pagination
              currentPage={page}
              data-testid="ads-manager-pagination"
              numberOfPages={Math.max(1, pagination.pageCount)}
              onChange={onPageChange}
            />
          </Row>
        )}
      </Column>
    </Loader>
  );
};
